import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";
import { Menu } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import ActionMenuItem from "@entrilia/shared/components/ActionMenuItem.tsx";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import DuplicateReportIcon from "@entrilia/shared/icons/DuplicateReportIcon.tsx";
import FolderManagedIcon from "@entrilia/shared/icons/FolderManagedIcon.tsx";
import biApi from "../../../../../../api/biApi.ts";
import type { ClientInfoExtended, ReportTemplate } from "../../../../../../api/types/svTypes.ts";
import { Status } from "../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../context/useClientContext.ts";
import { useReportTemplatesContext } from "../../contexts/ReportTemplatesContext.ts";
import { useGroupsContext } from "../../groups/contexts/GroupsContext.ts";
import DeleteTemplateDialog from "../DeleteTemplateDialog.tsx";
import DuplicateTemplateDialog from "../DuplicateTemplateDialog.tsx";
import RunWithCompanyDialog from "../RunWithCompanyDialog.tsx";
import SaveTemplateDialog from "../SaveTemplateDialog.tsx";
import SwitchStatusDialog from "../SwitchStatusDialog.tsx";

interface Props {
  anchorEl: HTMLElement | null;
  template: ReportTemplate;
  companies: ClientInfoExtended[];
  open: boolean;
  readonly?: boolean;
  onClose: () => void;
  getEditReportTemplateUrl: (organization: string, dataSource: string, templateId: string) => string;
}

export default function ReportTemplateActions({
  template,
  anchorEl,
  open,
  companies,
  onClose,
  getEditReportTemplateUrl,
  readonly,
}: Props) {
  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [openSwitchStatusDialog, setOpenSwitchStatusDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openRunWithDialog, setOpenRunWithDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const {
    actions: { switchStatus, reloadReportTemplates },
  } = useReportTemplatesContext();

  const { groups } = useGroupsContext();

  const changeStatus = useCallback(
    async (template: ReportTemplate, status: Status) => {
      setLoading(true);

      const result = await switchStatus(template, status);
      if (result) {
        sendNotification("The template status was successfully changed.");
      } else {
        sendNotificationError("An error occurred while changing the template status.");
      }

      setLoading(false);
      setOpenSwitchStatusDialog(false);
    },
    //eslint-disable-next-line @eslint-react/exhaustive-deps
    [switchStatus]
  );

  const handleEditTemplate = useCallback(() => {
    onClose();
    setOpenEditDialog(true);
  }, [onClose]);

  const handleDuplicateTemplate = useCallback(() => {
    onClose();
    setOpenDuplicateDialog(true);
  }, [onClose]);

  const handleChangeStatus = useCallback(() => {
    onClose();
    setOpenSwitchStatusDialog(true);
  }, [onClose]);

  const handleDeleteTemplate = useCallback(() => {
    onClose();
    setOpenDeleteDialog(true);
  }, [onClose]);

  const getRunTemplateHref = useMemo(() => {
    return getEditReportTemplateUrl(template.organization, template.dataSource, template.reportId);
  }, [template, getEditReportTemplateUrl]);

  const openRunWithCompany = useCallback(() => {
    onClose();
    setOpenRunWithDialog(true);
  }, [onClose]);

  const onRunTemplate = useCallback(() => {
    const hasNoAccessToDataSource = !companies.some((c) => c.clientCode === template.dataSource);
    if (hasNoAccessToDataSource || !template.dataSource) {
      openRunWithCompany();
      return;
    }
    onClose();
    window.open(getRunTemplateHref, "_blank", "noopener noreferrer");
  }, [onClose, template.dataSource, getRunTemplateHref, openRunWithCompany, companies]);

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={() => onClose()}>
        <ActionMenuItem
          key={"run"}
          icon={<PlayArrowRoundedIcon />}
          title="Run Report template"
          onClick={onRunTemplate}
          disabled={readonly}
        />
        <ActionMenuItem
          key={"edit"}
          icon={<FolderManagedIcon />}
          title="Edit template information"
          onClick={handleEditTemplate}
          disabled={readonly}
        />
        <ActionMenuItem
          key={"duplicate"}
          icon={<DuplicateReportIcon />}
          title="Duplicate"
          onClick={handleDuplicateTemplate}
        />
        <ActionMenuItem
          key={"status"}
          icon={<PublishedWithChangesRoundedIcon />}
          title={`Make ${template.status === Status.Live ? "Draft" : "Live"}`}
          onClick={handleChangeStatus}
        />
        <ActionMenuItem
          key={"delete"}
          icon={<DeleteOutlineOutlinedIcon color="error" />}
          title="Delete"
          onClick={handleDeleteTemplate}
        />
      </Menu>
      {openSwitchStatusDialog && template && (
        <SwitchStatusDialog
          templateName={template.name}
          currentStatus={template.status}
          onClose={() => setOpenSwitchStatusDialog(false)}
          onConfirm={(s) => changeStatus(template, s)}
          loading={loading}
        />
      )}
      {openDeleteDialog && template && (
        <DeleteTemplateDialog
          templates={[template]}
          onClose={() => setOpenDeleteDialog(false)}
          doDelete={(templates) =>
            biApi.deleteReportTemplates({
              templates: templates.map((t) => ({ templateId: t.reportId, organization: t.organization })),
            })
          }
          onDeleted={() => {
            sendNotification("The template was successfully deleted.");
            reloadReportTemplates();
          }}
        />
      )}
      {openRunWithDialog && template && (
        <RunWithCompanyDialog
          template={template}
          companies={companies}
          getEditReportTemplateUrl={getEditReportTemplateUrl}
          onClose={() => setOpenRunWithDialog(false)}
          onRun={() => setOpenRunWithDialog(false)}
        />
      )}
      {openEditDialog && template && (
        <SaveTemplateDialog
          title="Edit Template information"
          template={template}
          companies={companies}
          getTemplateConfigurationAsync={() => Promise.resolve(undefined)}
          onClose={() => setOpenEditDialog(false)}
          onSaved={() => {
            reloadReportTemplates();
            setOpenEditDialog(false);
            sendNotification("The template was successfully saved.");
          }}
        />
      )}
      {openDuplicateDialog && template && (
        <DuplicateTemplateDialog
          organization={clientCode}
          groups={groups[template.organization] || []}
          template={template}
          onClose={() => setOpenDuplicateDialog(false)}
          onChanged={() => {
            reloadReportTemplates();
            setOpenDuplicateDialog(false);
            sendNotification("The template was successfully duplicated.");
          }}
        />
      )}
    </>
  );
}
